import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

type StatusData = {
  overall_status: 'operational' | 'degraded' | 'major_outage' | 'unknown';
  components: { name: string; status: 'operational' | 'degraded' | 'major_outage' }[];
  ongoing_incidents: { name: string; severity: string; status: string }[];
  in_progress_maintenances: { name: string; status: string }[];
};

const STATUS_COLORS = {
  operational: 'bg-accent',
  degraded: 'bg-orange-500',
  major_outage: 'bg-destructive',
  unknown: 'bg-muted-foreground',
} as const;

const STATUS_PULSE = {
  operational: 'bg-accent/60',
  degraded: 'bg-orange-500/60',
  major_outage: 'bg-destructive/60',
  unknown: 'bg-muted-foreground/60',
} as const;

const STATUS_LABELS = {
  operational: 'Operationeel',
  degraded: 'Verstoord',
  major_outage: 'Storing',
  unknown: 'Onbekend',
} as const;

const COMPONENT_STATUS_LABELS = {
  operational: 'Operationeel',
  degraded: 'Verstoord',
  major_outage: 'Storing',
} as const;

export const StatusDot = () => {
  const [status, setStatus] = useState<StatusData | null>(null);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchStatus = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('status-check');
      if (error) throw error;
      setStatus(data);
    } catch (e) {
      console.error('Failed to fetch status:', e);
      setStatus({
        overall_status: 'unknown',
        components: [],
        ongoing_incidents: [],
        in_progress_maintenances: [],
      });
    }
  };

  const overallStatus = status?.overall_status || 'unknown';
  const dotColor = STATUS_COLORS[overallStatus];
  const pulseColor = STATUS_PULSE[overallStatus];
  const isNotOperational = overallStatus !== 'operational' && overallStatus !== 'unknown';

  return (
    <Popover>
      <PopoverTrigger className="absolute -bottom-0.5 -right-0.5 focus:outline-none cursor-pointer z-10">
        <span className="relative flex h-2.5 w-2.5">
          <span className={cn("animate-ping absolute inline-flex h-full w-full rounded-full opacity-75", pulseColor)} />
          <span className={cn("relative inline-flex rounded-full h-2.5 w-2.5 border border-background", dotColor)} />
        </span>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 bg-card border-border p-0 z-[60]">
        <div className="p-3 border-b border-border">
          <div className="flex items-center gap-2">
            <span className={cn("h-2.5 w-2.5 rounded-full", dotColor)} />
            <span className="text-sm font-semibold text-card-foreground">
              {STATUS_LABELS[overallStatus]}
            </span>
          </div>
          {isNotOperational && (
            <p className="text-xs text-muted-foreground mt-1.5">
              De app werkt mogelijk niet volledig.
            </p>
          )}
        </div>

        {status?.components && status.components.length > 0 && (
          <div className="p-3 space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Systemen</p>
            {status.components.map((comp) => (
              <div key={comp.name} className="flex items-center justify-between">
                <span className="text-sm text-card-foreground">{comp.name}</span>
                <div className="flex items-center gap-1.5">
                  <span className={cn("h-2 w-2 rounded-full", STATUS_COLORS[comp.status] || STATUS_COLORS.unknown)} />
                  <span className={cn("text-xs", comp.status === 'operational' ? 'text-muted-foreground' : 'text-orange-500')}>
                    {COMPONENT_STATUS_LABELS[comp.status] || 'Onbekend'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {status?.ongoing_incidents && status.ongoing_incidents.length > 0 && (
          <div className="p-3 border-t border-border space-y-1.5">
            <p className="text-xs font-medium text-destructive uppercase tracking-wider">Actieve incidenten</p>
            {status.ongoing_incidents.map((incident, i) => (
              <p key={i} className="text-xs text-card-foreground">{incident.name}</p>
            ))}
          </div>
        )}

        {status?.in_progress_maintenances && status.in_progress_maintenances.length > 0 && (
          <div className="p-3 border-t border-border space-y-1.5">
            <p className="text-xs font-medium text-orange-500 uppercase tracking-wider">Onderhoud</p>
            {status.in_progress_maintenances.map((m, i) => (
              <p key={i} className="text-xs text-card-foreground">{m.name}</p>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
